import { isReportable, parseDsn, type ParsedDsn } from './dsn';
import { Scope } from './scope';
import { errorMessage, errorType, parseStacktrace } from './stacktrace';
import { Transport } from './transport';
import type {
  Breadcrumb,
  CaptureHint,
  EventUser,
  Level,
  OutpostEvent,
  OutpostOptions,
} from './types';

export interface ClientMeta {
  name: string;
  version: string;
  platform: string;
}

/**
 * The SDK core, shared by the browser and Node packages.
 *
 * Everything platform-specific (global handlers, breadcrumb sources) lives in
 * those packages; this holds the logic that must behave identically everywhere.
 */
export class Client {
  readonly scope: Scope;
  private dsn?: ParsedDsn;
  private transport?: Transport;

  constructor(
    private options: OutpostOptions,
    private meta: ClientMeta,
  ) {
    this.scope = new Scope(options.maxBreadcrumbs ?? 100);

    if (!options.dsn) {
      // No DSN is a legitimate configuration — it is how you disable reporting
      // in local development without branching every call site.
      this.log('no DSN configured; events will not be sent');
      return;
    }

    this.dsn = parseDsn(options.dsn);
    this.transport = new Transport({
      dsn: this.dsn,
      clientName: meta.name,
      version: meta.version,
      timeout: options.timeout,
      debug: options.debug,
    });
  }

  /** Whether events will actually be delivered. */
  get enabled(): boolean {
    return Boolean(this.transport);
  }

  /** Report an exception. Resolves to the event id, or null if not sent. */
  async captureException(error: unknown, hint?: CaptureHint): Promise<string | null> {
    const stack = error instanceof Error ? error.stack : undefined;

    const event = this.buildEvent({
      level: 'error',
      ...hint?.event,
      exception: [
        {
          type: errorType(error),
          value: errorMessage(error),
          stacktrace: parseStacktrace(stack),
          mechanism: {
            type: hint?.mechanism ?? 'generic',
            // Default to handled: an uncaught error is reported by a global
            // handler, which passes handled: false explicitly.
            handled: hint?.handled ?? true,
          },
        },
      ],
    });

    return this.dispatch(event);
  }

  /** Report a message with no associated exception. */
  async captureMessage(message: string, level: Level = 'info'): Promise<string | null> {
    return this.dispatch(this.buildEvent({ level, message }));
  }

  /** Report a fully-formed event. */
  async captureEvent(event: Partial<OutpostEvent>): Promise<string | null> {
    return this.dispatch(this.buildEvent(event));
  }

  addBreadcrumb(breadcrumb: Breadcrumb): void {
    this.scope.addBreadcrumb(breadcrumb);
  }

  setUser(user: EventUser | undefined): void {
    this.scope.setUser(user);
  }

  /** Pass `undefined` to remove the tag. */
  setTag(key: string, value: string | undefined): void {
    this.scope.setTag(key, value);
  }

  /** Deliver anything buffered. Call before a process exits. */
  async flush(): Promise<void> {
    await this.transport?.flush();
  }

  /** Best-effort delivery during page unload. */
  sendBeacon(event: OutpostEvent): boolean {
    return this.transport?.sendBeacon(event) ?? false;
  }

  /** Merge scope and configuration into a complete event. */
  private buildEvent(partial: Partial<OutpostEvent>): OutpostEvent {
    const snapshot = this.scope.snapshot();

    return {
      event_id: randomId(),
      timestamp: new Date().toISOString(),
      platform: this.meta.platform,
      level: 'error',
      environment: this.options.environment,
      release: this.options.release,
      dist: this.options.dist,
      sdk: { name: this.meta.name, version: this.meta.version },
      user: snapshot.user,
      breadcrumbs: snapshot.breadcrumbs.length ? snapshot.breadcrumbs : undefined,
      contexts: Object.keys(snapshot.contexts).length ? snapshot.contexts : undefined,
      extra: Object.keys(snapshot.extra).length ? snapshot.extra : undefined,
      ...partial,
      // Merged last so per-event tags win over scope tags, which win over
      // options tags.
      tags: { ...this.options.tags, ...snapshot.tags, ...partial.tags },
    };
  }

  private async dispatch(event: OutpostEvent): Promise<string | null> {
    if (!this.transport) return null;

    if (!isReportable(event)) {
      this.log('dropping event with neither exception nor message');
      return null;
    }

    if (this.isIgnored(event)) {
      this.log('dropping ignored error');
      return null;
    }

    // Sampling before beforeSend, so a scrubbing hook is not run for events
    // that are about to be discarded anyway.
    const sampleRate = this.options.sampleRate ?? 1;
    if (sampleRate < 1 && Math.random() >= sampleRate) {
      return null;
    }

    let finalEvent: OutpostEvent | null = event;
    if (this.options.beforeSend) {
      try {
        finalEvent = this.options.beforeSend(event);
      } catch (error) {
        // A throwing hook must not take down the host application, and must not
        // silently send the unscrubbed event either.
        this.log('beforeSend threw; dropping the event', error);
        return null;
      }
    }
    if (!finalEvent) return null;

    const result = await this.transport.send(finalEvent);
    return result.ok ? (finalEvent.event_id ?? null) : null;
  }

  private isIgnored(event: OutpostEvent): boolean {
    const patterns = this.options.ignoreErrors;
    if (!patterns?.length) return false;

    const haystack = [event.message, event.exception?.[0]?.value, event.exception?.[0]?.type]
      .filter(Boolean)
      .join(' ');

    return patterns.some((pattern) => haystack.includes(pattern));
  }

  private log(...args: unknown[]): void {
    if (this.options.debug) {
      // eslint-disable-next-line no-console
      console.warn('[outpost]', ...args);
    }
  }
}

/** A UUID for the event id, falling back where `crypto` is unavailable. */
export function randomId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();

  // Older Safari and some embedded runtimes lack randomUUID.
  const bytes = new Uint8Array(16);
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Set the version and variant bits so the result is a well-formed UUIDv4.
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
